import { useState, useEffect } from 'react'
import { invoicesAPI } from '../../services/api'

import { logger } from '@/utils/logger'

const statusLabels: Record<string, string> = {
  NEW: 'Neu',
  DUE: 'Fällig',
  OVERDUE: 'Überfällig',
  REMINDED: 'Angemahnt',
  PAID: 'Bezahlt',
}

const statusTagClass: Record<string, string> = {
  NEW: 'wc-tag-outline',
  DUE: 'wc-tag-outline',
  OVERDUE: 'wc-tag-neutral',
  REMINDED: 'wc-tag-outline',
  PAID: 'wc-tag-accent',
}

export default function CustomerInvoicesPage() {
  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    loadInvoices()
  }, [])

  const loadInvoices = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await invoicesAPI.getMy()
      setInvoices(response.data)
    } catch (err: any) {
      logger.error('Error loading invoices:', err)
      setError('Fehler beim Laden der Rechnungen')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (invoice: any) => {
    if (!invoice.file_path) {
      setError('Keine PDF-Datei verfügbar')
      return
    }

    try {
      const response = await invoicesAPI.downloadMy(invoice.id)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', invoice.file_name || `invoice_${invoice.invoice_number}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err: any) {
      logger.error('Error downloading invoice:', err)
      setError('Fehler beim Herunterladen der Rechnung')
    }
  }

  const getStatusTag = (status: string) => (
    <span className={`wc-tag ${statusTagClass[status] || statusTagClass.NEW}`}>
      {statusLabels[status] || status}
    </span>
  )

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('de-DE')
  }

  const formatCurrency = (amount: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)

  const calculateSummary = () => {
    const openInvoices = invoices.filter((inv) => ['NEW', 'DUE', 'OVERDUE', 'REMINDED'].includes(inv.status))
    const paidInvoices = invoices.filter((inv) => inv.status === 'PAID')
    const currentYear = new Date().getFullYear()
    const yearTotal = invoices
      .filter((inv) => new Date(inv.issue_date).getFullYear() === currentYear)
      .reduce((sum, inv) => sum + inv.amount, 0)

    return { openCount: openInvoices.length, paidCount: paidInvoices.length, yearTotal }
  }

  const summary = calculateSummary()

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, margin: '0 0 6px' }}>Rechnungen</h1>
        <p className="wc-muted" style={{ fontSize: 14, margin: 0 }}>Übersicht über alle Ihre Rechnungen und Zahlungen</p>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 24 }}>{error}</div>}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 32 }}>
        <div className="wc-card wc-elev-sm" style={{ gap: 6 }}>
          <span className="wc-muted" style={{ fontSize: 13 }}>Offene Rechnungen</span>
          <span style={{ fontSize: 28 }}>{summary.openCount}</span>
        </div>
        <div className="wc-card wc-elev-sm" style={{ gap: 6 }}>
          <span className="wc-muted" style={{ fontSize: 13 }}>Bezahlte Rechnungen</span>
          <span style={{ fontSize: 28, color: 'var(--wc-accent-300)' }}>{summary.paidCount}</span>
        </div>
        <div className="wc-card wc-elev-sm" style={{ gap: 6 }}>
          <span className="wc-muted" style={{ fontSize: 13 }}>Gesamt (Jahr)</span>
          <span style={{ fontSize: 28 }}>{formatCurrency(summary.yearTotal)}</span>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="wc-card wc-elev-sm" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <h3 className="wc-card-title" style={{ marginBottom: 8 }}>Keine Rechnungen</h3>
          <p className="wc-muted" style={{ marginBottom: 20 }}>
            Sie haben derzeit keine Rechnungen. Rechnungen werden automatisch erstellt,<br />
            wenn Sie ein App-Abonnement abschließen.
          </p>
          <a href="/apps" className="wc-btn wc-btn-primary">Apps entdecken</a>
        </div>
      ) : (
        <div className="wc-card wc-elev-sm" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Rechnungsnr.</th>
                  <th>Rechnungsdatum</th>
                  <th>Fälligkeitsdatum</th>
                  <th>Beschreibung</th>
                  <th>Betrag</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoice_number}</td>
                    <td className="wc-muted">{formatDate(invoice.issue_date)}</td>
                    <td className="wc-muted">{formatDate(invoice.due_date)}</td>
                    <td>{invoice.description || '-'}</td>
                    <td>{formatCurrency(invoice.amount)}</td>
                    <td>{getStatusTag(invoice.status)}</td>
                    <td>
                      {invoice.file_path ? (
                        <button onClick={() => handleDownload(invoice)} className="wc-btn wc-btn-ghost" style={{ padding: 0, fontSize: 13 }}>
                          PDF herunterladen
                        </button>
                      ) : (
                        <span className="wc-muted" style={{ fontSize: 13 }}>Keine Datei</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment methods */}
      <div className="wc-card wc-elev-sm" style={{ marginTop: 24 }}>
        <h3 className="wc-card-title">Zahlungsmethoden</h3>
        <p className="wc-muted" style={{ fontSize: 13 }}>Verwalten Sie Ihre Zahlungsmethoden für automatische Abbuchungen.</p>
        <div className="wc-alert wc-alert-info" style={{ margin: 0 }}>
          <strong>Hinweis:</strong> Die Verwaltung von Zahlungsmethoden wird in Kürze verfügbar sein. Aktuell erfolgt die Zahlung über Rechnung.
        </div>
      </div>
    </div>
  )
}
